import { EmitterBatch } from "../../core/batching/emitter_batch.js";
import { ShaderBuilder } from "../../shader_builder/shader_builder.js";
import { Vector2 } from "../../../common/vector2.js";
import { randomPointInCircle } from "../../../common/visual_fx_common_funcs.js";
import { BlendMode, AttributeSize } from "../../core/types/webgl_types.js";
import { FireworksEmitter } from "../fireworks/fireworks_types.js";
import { Vfx2DGlobalAtlas } from "../../core/rendering/textures/vfx_2d_global_atlas.js";
import { WebGLStaticAtlasBuilder } from "../../core/rendering/textures/webgl_static_atlas_bulder.js";

export const ParticleLayout = {
  a_position: AttributeSize.Vec2,
  a_color: AttributeSize.Vec3,
  a_size_range: AttributeSize.Vec2,
  a_alpha_range: AttributeSize.Vec2,
  a_breakpoint: AttributeSize.Float,
  a_progress: AttributeSize.Float,
  a_min_uv: AttributeSize.Vec2,
  a_max_uv: AttributeSize.Vec2,
} as const;

export class VfxParticleNormalBatch extends EmitterBatch<FireworksEmitter, typeof ParticleLayout>
{
  constructor( blend_mode: BlendMode = BlendMode.Normal, max_instances: number = 400 )
  {
    super( {
      max_instances,
      vertices_per_instance: 1,
      primitive_type: WebGL2RenderingContext.POINTS,
      blend_mode,
      layout: ParticleLayout,
    } );

    this.buildShader(
      new ShaderBuilder()
        .includeChunk( 'remap' )
        .withUniform( 'sampler2D', 'u_texture' )
        .withVarying( 'vec3', 'v_color' )
        .withVarying( 'vec2', 'v_alpha_range' )
        .withVarying( 'float', 'v_breakpoint' )
        .withVarying( 'float', 'v_progress' )
        .withVarying( 'vec2', 'v_min_uv' )
        .withVarying( 'vec2', 'v_max_uv' )
        .withVertexLogic( `
          float t = a_progress;
          position = a_position;
          size = mix(a_size_range.x, a_size_range.y, t);
          v_color = a_color;
          v_alpha_range = a_alpha_range;
          v_breakpoint = a_breakpoint;
          v_progress = t;
          v_min_uv = a_min_uv;
          v_max_uv = a_max_uv;
        ` )
        .withFragmentLogic( `
          vec2 uv = mix(v_min_uv, v_max_uv, gl_PointCoord);
          vec4 tex = texture(u_texture, uv);
          float fade = remap(v_progress, v_breakpoint, 1.0, v_alpha_range.x, v_alpha_range.y, true);
          float alpha = tex.a * fade;
          fragColor = vec4(v_color * alpha, alpha);
        ` )
    );
  }

  protected override onInit( gl: WebGL2RenderingContext ): void
  {
    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const atlas = WebGLStaticAtlasBuilder.createAtlasFromData( gl, atlas_data );
    this.textures = [ { location: 'u_texture', texture: atlas.texture, unit: 0 } ];
  }

  protected override updateEmitter( emitter: FireworksEmitter, now: number ): void
  {
    let t = emitter.getProgress( now );
    if ( emitter.payload.ease )
    {
      t = emitter.payload.ease( t );
    }
    const pos = emitter.payload.precomputed.catmullRomAtT( t );
    emitter.setPosition( pos );
  }

  protected override emitInstance( spawn_pos: Vector2, spawn_time: number, emitter: FireworksEmitter ): void
  {
    const cfg = emitter.payload.config;
    let point = spawn_pos;
    if ( cfg.circle_radius && cfg.circle_radius > 0 )
    {
      point = randomPointInCircle( spawn_pos, cfg.circle_radius );
    }

    const idx = this.allocateInstanceIndex( spawn_time, cfg.particle_life_span );
    if ( idx === -1 ) return;

    const atlas_data = Vfx2DGlobalAtlas.getAtlasData();
    const bounds = atlas_data.getUVRect( cfg.img_id );

    const data = this.buffer.getDataArray();
    const offset = idx * this.buffer.floats_per_instance;

    data[offset + this.attr_offset.a_position] = point.x;
    data[offset + this.attr_offset.a_position + 1] = point.y;
    data[offset + this.attr_offset.a_color] = emitter.payload.color.x;
    data[offset + this.attr_offset.a_color + 1] = emitter.payload.color.y;
    data[offset + this.attr_offset.a_color + 2] = emitter.payload.color.z;
    data[offset + this.attr_offset.a_size_range] = cfg.start_size;
    data[offset + this.attr_offset.a_size_range + 1] = cfg.end_size;
    data[offset + this.attr_offset.a_alpha_range] = cfg.start_alpha;
    data[offset + this.attr_offset.a_alpha_range + 1] = cfg.end_alpha;
    data[offset + this.attr_offset.a_breakpoint] = cfg.breakpoint;
    data[offset + this.attr_offset.a_progress] = 0.0;
    data[offset + this.attr_offset.a_min_uv] = bounds.min_x;
    data[offset + this.attr_offset.a_min_uv + 1] = bounds.min_y;
    data[offset + this.attr_offset.a_max_uv] = bounds.max_x;
    data[offset + this.attr_offset.a_max_uv + 1] = bounds.max_y;
  }

  protected override updateInstance( gpu_data: Float32Array, offset: number, progress: number ): boolean
  {
    gpu_data[offset + this.attr_offset.a_progress] = progress;
    return true;
  }
}

export class VfxParticleAdditiveBatch extends VfxParticleNormalBatch
{
  constructor()
  {
    super( BlendMode.Additive, 15600 );
  }
}
